import { TableState } from "@tanstack/react-table"
import { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import {
	MdKeyboardArrowDown,
	MdKeyboardArrowLeft,
	MdKeyboardArrowRight,
	MdKeyboardDoubleArrowLeft,
	MdKeyboardDoubleArrowRight,
} from "react-icons/md"

type PaginationIconId = "first" | "previous" | "next" | "last" | "down"

type PaginationButtonProps = {
	disabledCondition: boolean
	iconId: PaginationIconId
	onClickHandler: () => void
	positionRef: string
}

export const PaginationButton = (props: PaginationButtonProps): ReactElement => {
	return (
		<button
			onClick={props.onClickHandler}
			disabled={!props.disabledCondition}
			className={`relative ${
				props.positionRef === "right" ? "right-0" : "left-0"
			} text-dipai-text-secondary disabled:text-dipai-text-disabled hover:text-dipai-text-highlight absolute h-8 w-8 hover:scale-110 hover:cursor-pointer disabled:hover:scale-100 disabled:hover:cursor-default`}
		>
			{props.iconId === "first" ? (
				<MdKeyboardDoubleArrowLeft className={`h-6 w-6`} />
			) : props.iconId === "last" ? (
				<MdKeyboardDoubleArrowRight className={`h-6 w-6`} />
			) : props.iconId === "previous" ? (
				<MdKeyboardArrowLeft className={`h-6 w-6`} />
			) : props.iconId === "next" ? (
				<MdKeyboardArrowRight className={`h-6 w-6`} />
			) : (
				<MdKeyboardArrowDown className={`h-6 w-6`} />
			)}
		</button>
	)
}

export type PaginationControlsProps = {
	getCanNextPage: () => boolean
	getCanPreviousPage: () => boolean
	getPageCount: () => number
	nRecords: number
	nextPage: () => void
	previousPage: () => void
	setPageIndex: (updater: number | ((pageIndex: number) => number)) => void
	setPageSize: (pageSize: number) => void
	tableState: TableState
	translationFunc: ReturnType<typeof useTranslation>["t"]
}

export const PaginationButtonsGroup = (
	props: Omit<PaginationControlsProps, "setPageSize" | "nRecords">
): ReactElement => {
	return (
		<div className="grid grid-cols-7 gap-2">
			{/* TO FIRST PAGE */}
			<PaginationButton
				disabledCondition={props.getCanPreviousPage()}
				onClickHandler={() => props.setPageIndex(0)}
				iconId={"first"}
				positionRef={"right"}
			/>
			{/* TO PREVIOUS PAGE */}
			<PaginationButton
				disabledCondition={props.getCanPreviousPage()}
				onClickHandler={() => props.previousPage()}
				iconId={"previous"}
				positionRef={"right"}
			/>
			{/* CURRENT PAGE DISPLAY */}
			<div className="col-span-3 h-8 text-center">
				<h1 className="text-menu text-dipai-text-primary inline-block h-8 py-1">
					{`${props.translationFunc("PAGE")} ${
						props.tableState.pagination.pageIndex + 1
					} ${props.translationFunc("OF")} ${props.getPageCount()}`}
				</h1>
			</div>
			{/* TO NEXT PAGE */}
			<PaginationButton
				disabledCondition={props.getCanNextPage()}
				onClickHandler={() => props.nextPage()}
				iconId={"next"}
				positionRef={"left"}
			/>
			{/* TO LAST PAGE */}
			<PaginationButton
				disabledCondition={props.getCanNextPage()}
				onClickHandler={() => props.setPageIndex(props.getPageCount() - 1)}
				iconId={"last"}
				positionRef={"left"}
			/>
		</div>
	)
}

export function PaginationControls(props: PaginationControlsProps): ReactElement {
	return (
		<div
			className={`grid h-12.5 w-full grid-cols-[1fr_18rem_1fr] items-center justify-end gap-4 bg-white px-4`}
		>
			{/* RIGHT AREA */}
			<div />
			{/* CENTRAL AREA */}
			<PaginationButtonsGroup {...props} />
			{/* LEFT AREA */}
			<div className="flex flex-row items-center justify-end gap-4">
				<p className={`text-dipai-text-primary`}>{`${
					1 + props.tableState.pagination.pageSize * props.tableState.pagination.pageIndex
				} - ${
					props.tableState.pagination.pageSize +
						props.tableState.pagination.pageSize *
							props.tableState.pagination.pageIndex <=
					props.nRecords
						? props.tableState.pagination.pageSize +
							props.tableState.pagination.pageSize *
								props.tableState.pagination.pageIndex
						: props.nRecords
				} of ${props.nRecords}`}</p>
				{/* AMOUNT TO DISPLAY DROPDOWN */}
				<select
					value={props.tableState.pagination.pageSize}
					className="border-dipai-text-muted text-small hover:border-dipai-text-highlight focus:border-dipai-text-highlight text-dipai-text-primary mr-px h-8 w-30 rounded-sm border-1 py-0.5 pr-10 pl-3 hover:cursor-pointer focus:mr-0 focus:w-[calc(7.5rem+0.0625rem)] focus:border-2 focus:pl-[calc(0.75rem-0.0625rem)] focus:ring-0 focus:outline-hidden"
					onChange={(e) => props.setPageSize(Number(e.target.value))}
				>
					{[10, 25, 50].map((pageSize) => (
						<option
							key={pageSize}
							value={pageSize}
						>
							{`${props.translationFunc("SHOW")} ${pageSize}`}
						</option>
					))}
				</select>
			</div>
		</div>
	)
}
